// La RECETA de una generación de Redes — ADR-2026-10-04-rehacer-desde-la-cuadricula.
//
// Lo que había en la caja cuando se tocó Generar: el prompt tal como lo escribió el
// usuario (con sus `@Nombre`, ANTES del metaprompting y de traducir las menciones), los
// adjuntos (producto, modelo, imágenes, referencias) y los ajustes del medio. Viaja en el
// body del pedido como `receta_de_redes`; el backend guarda el body entero en
// `imagenes_generadas.datos_generacion`, así que cada creación trae la suya y "Rehacer"
// la vuelve a poner en la caja. Puro: armar y leer (con todo lo que puede venir roto).

import type { ChatAttachment } from '../../agentAttachments';
import type { AjustesDeRedes, Medio } from './redes';

export const CLAVE_DE_RECETA = 'receta_de_redes';

export interface RecetaDeRedes {
  v: 1;
  medio: Medio;
  /** Como estaba en la caja: lo que el usuario mandó, no lo que redactó el escritor. */
  prompt: string;
  adjuntos: ChatAttachment[];
  /** Los ajustes del medio con que se generó (los del otro medio no se guardan). */
  ajustes: AjustesDeRedes['imagen'] | AjustesDeRedes['video'];
}

/** Sólo lo que la caja de Redes sabe adjuntar; el resto de los `kind` son del chat. */
const KINDS_DE_REDES = new Set(['image', 'product', 'model', 'referencia', 'video']);

export function armarReceta(prompt: string, adjuntos: ChatAttachment[], ajustes: AjustesDeRedes): RecetaDeRedes {
  return {
    v: 1,
    medio: ajustes.medio,
    prompt: prompt.trim(),
    adjuntos: adjuntos.filter((a) => KINDS_DE_REDES.has(a.kind)),
    ajustes: ajustes.medio === 'video' ? ajustes.video : ajustes.imagen,
  };
}

const esObjeto = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);
const esTexto = (v: unknown): v is string => typeof v === 'string' && v.length > 0;

/** Un adjunto guardado, sólo si trae lo que su `kind` necesita para volver a usarse. */
function leerAdjunto(v: unknown): ChatAttachment | null {
  if (!esObjeto(v) || !esTexto(v.id)) return null;
  switch (v.kind) {
    case 'image':
      return esTexto(v.storagePath)
        ? {
            kind: 'image',
            id: v.id,
            storagePath: v.storagePath,
            previewUrl: typeof v.previewUrl === 'string' ? v.previewUrl : '',
            ...(esTexto(v.bucket) ? { bucket: v.bucket } : {}),
          }
        : null;
    case 'product':
      return esTexto(v.productId)
        ? { kind: 'product', id: v.id, productId: v.productId, name: typeof v.name === 'string' ? v.name : 'Producto', imageUrl: typeof v.imageUrl === 'string' ? v.imageUrl : '' }
        : null;
    case 'model':
      return esObjeto(v.selection)
        ? ({ kind: 'model', id: v.id, selection: v.selection, name: typeof v.name === 'string' ? v.name : 'Modelo', imageUrl: typeof v.imageUrl === 'string' ? v.imageUrl : null } as ChatAttachment)
        : null;
    case 'referencia':
      return esTexto(v.imageUrl) && (v.tipo === 'pose' || v.tipo === 'fondo' || v.tipo === 'moodboard' || v.tipo === 'elemento' || v.tipo === 'tipografia')
        ? { kind: 'referencia', id: v.id, tipo: v.tipo, name: typeof v.name === 'string' ? v.name : '', imageUrl: v.imageUrl }
        : null;
    case 'video':
      return esTexto(v.storagePath)
        ? {
            kind: 'video',
            id: v.id,
            name: typeof v.name === 'string' ? v.name : 'Video',
            storagePath: v.storagePath,
            ...(esTexto(v.bucket) ? { bucket: v.bucket } : {}),
            bytes: 0,
            // Se conserva: de ella dependen los topes del video de referencia.
            duracionSeg: typeof v.duracionSeg === 'number' && v.duracionSeg > 0 ? v.duracionSeg : 0,
            ...(esTexto(v.previewUrl) ? { previewUrl: v.previewUrl } : {}),
          }
        : null;
    default:
      return null;
  }
}

/**
 * La receta de una creación, o `null` si no la tiene (lo generado antes de este cambio, o
 * en otro modo) o vino rota. `datosGeneracion` puede llegar como objeto o como JSON en
 * texto (filas viejas). Un adjunto que no se puede leer se descarta; el resto vale.
 */
export function leerReceta(datosGeneracion: unknown): RecetaDeRedes | null {
  let datos: unknown = datosGeneracion;
  if (typeof datos === 'string') {
    try {
      datos = JSON.parse(datos);
    } catch {
      return null;
    }
  }
  if (!esObjeto(datos)) return null;
  const r = datos[CLAVE_DE_RECETA];
  if (!esObjeto(r) || r.v !== 1) return null;
  if (r.medio !== 'imagen' && r.medio !== 'video') return null;
  if (typeof r.prompt !== 'string' || !esObjeto(r.ajustes) || !esTexto(r.ajustes.formatSlug)) return null;
  const adjuntos = Array.isArray(r.adjuntos) ? r.adjuntos.flatMap((a) => leerAdjunto(a) ?? []) : [];
  // Sin texto ni adjuntos no hay nada que rehacer.
  if (!r.prompt.trim() && adjuntos.length === 0) return null;
  return { v: 1, medio: r.medio, prompt: r.prompt, adjuntos, ajustes: r.ajustes as RecetaDeRedes['ajustes'] };
}

/**
 * Los ajustes de la caja con los de la receta puestos: cambia al medio de la receta y pisa
 * SÓLO los ajustes de ese medio (los del otro se conservan). Lo que no venga bien tipado
 * queda como estaba.
 */
export function ajustesConReceta(actuales: AjustesDeRedes, receta: RecetaDeRedes): AjustesDeRedes {
  const a = receta.ajustes as Record<string, unknown>;
  if (receta.medio === 'video') {
    const guardados = esObjeto(a.ajustes) ? a.ajustes : {};
    const v = actuales.video;
    return {
      ...actuales,
      medio: 'video',
      video: {
        formatSlug: esTexto(a.formatSlug) ? a.formatSlug : v.formatSlug,
        tipoVideo: esTexto(a.tipoVideo) ? a.tipoVideo : v.tipoVideo,
        ajustes: {
          ...v.ajustes,
          ...(guardados.quality === 'basica' || guardados.quality === 'profesional' ? { quality: guardados.quality } : {}),
          ...(guardados.resolution === '480p' || guardados.resolution === '720p' || guardados.resolution === '1080p'
            ? { resolution: guardados.resolution }
            : {}),
          ...(typeof guardados.durationSeconds === 'number' && guardados.durationSeconds > 0
            ? { durationSeconds: guardados.durationSeconds }
            : {}),
        },
      },
    };
  }
  const i = actuales.imagen;
  return {
    ...actuales,
    medio: 'imagen',
    imagen: {
      formatSlug: esTexto(a.formatSlug) ? a.formatSlug : i.formatSlug,
      calidad: a.calidad === '1K' || a.calidad === '2K' || a.calidad === '4K' ? a.calidad : i.calidad,
      cantidad: typeof a.cantidad === 'number' && a.cantidad >= 1 ? Math.floor(a.cantidad) : i.cantidad,
    },
  };
}
